import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, User, Phone, Building2, X, Mail } from 'lucide-react';
import globalSearchService from '../../api/crm/globalSearchService';
import useDebounce from '../../hooks/useDebounce';
import { getLeadStatusColor } from '../../utils/constants';

const initialFilters = {
  search: '',
  recordType: ''
};

const sortLabels = {
  recordId: 'Record ID',
  recordType: 'Record Type',
  name: 'Customer/Lead Details',
  status: 'Status',
  owner: 'Owner',
  createdAt: 'Created At'
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getOwnerName = (record) => {
  const ownerName = record.ownerName?.trim();
  if (ownerName) return ownerName;

  const firstName = record.owner?.firstName || '';
  const lastName = record.owner?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || record.owner?.email || '-';
};

const getOwnerEmail = (record) => record.owner?.email || '';

const getStatusLabel = (status) => {
  if (!status) return '-';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const getGlobalStatusColor = (status, recordType) => {
  const statusLabel = getStatusLabel(status);

  if (recordType === 'Lead') {
    return getLeadStatusColor(statusLabel);
  }

  const customerStatusColors = {
    Active: 'bg-emerald-100 text-emerald-800',
    Inactive: 'bg-gray-100 text-gray-800'
  };

  return customerStatusColors[statusLabel] || getLeadStatusColor(statusLabel);
};

const GlobalCRMSearch = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [records, setRecords] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debouncedFilters = useDebounce(filters, 300);

  const hasSearchTrigger = useMemo(
    () => String(debouncedFilters.search || '').trim() !== '',
    [debouncedFilters]
  );

  const showClear = useMemo(
    () => Object.values(filters).some((value) => String(value || '').trim() !== ''),
    [filters]
  );

  const fetchRecords = async ({ page = pagination.page, nextSortBy = sortBy, nextSortOrder = sortOrder } = {}) => {
    const controller = new AbortController();
    setLoading(true);
    setError('');

    try {
      const result = await globalSearchService.search({
        ...debouncedFilters,
        page,
        limit: itemsPerPage,
        sortBy: nextSortBy,
        sortOrder: nextSortOrder
      }, controller.signal);

      if (result.success) {
        setRecords(result.records);
        setPagination(result.pagination);
      } else {
        setRecords([]);
        setError(result.error || 'Failed to search CRM records');
      }
    } catch (err) {
      setRecords([]);
      setError(err.message || 'Failed to search CRM records');
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  };

  useEffect(() => {
    if (!hasSearchTrigger) {
      setRecords([]);
      setPagination((current) => ({ ...current, page: 1, total: 0, totalPages: 1 }));
      setError('');
      setLoading(false);
      return;
    }

    fetchRecords({ page: 1 });
  }, [debouncedFilters, hasSearchTrigger, sortBy, sortOrder, itemsPerPage]);

  const handlePageChange = (page) => {
    fetchRecords({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPagination((current) => ({ ...current, page: 1, limit }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleClear = () => {
    setFilters(initialFilters);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortOrder(column === 'createdAt' ? 'desc' : 'asc');
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="w-4 h-4" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lead & Customer Search</h1>
          <p className="text-sm text-gray-600">Find CRM leads and customers by contact, business, or location.</p>
        </div>
      </div>

      <form onSubmit={(event) => event.preventDefault()} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
          <label className="block lg:flex-1">
            <span className="text-sm font-medium text-gray-700">Search</span>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={filters.search}
                onChange={(event) => handleFilterChange('search', event.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-agrosaf-green focus:outline-none focus:ring-1 focus:ring-agrosaf-green"
                placeholder="Search mobile number, email, customer/lead name, or business name"
              />
            </div>
          </label>
          <label className="block xl:w-44">
            <span className="text-sm font-medium text-gray-700">Record Type</span>
            <select
              value={filters.recordType}
              onChange={(event) => handleFilterChange('recordType', event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-agrosaf-green focus:outline-none focus:ring-1 focus:ring-agrosaf-green"
            >
              <option value="">All records</option>
              <option value="Lead">Leads</option>
              <option value="Customer">Customers</option>
            </select>
          </label>
          <div className="flex flex-wrap items-center gap-3 xl:pt-7">
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {Object.entries(sortLabels).map(([key, label]) => (
                  <th key={key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <button type="button" onClick={() => handleSort(key)} className="inline-flex items-center gap-1">
                      {label}
                      {getSortIcon(key)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    <RefreshCw className="mx-auto mb-3 h-6 w-6 animate-spin text-agrosaf-green" />
                    Loading records...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-red-600">{error}</td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                    {hasSearchTrigger
                      ? 'No matching leads or customers found. Try adjusting the search or filters.'
                      : 'Start by entering a mobile number, email, customer or lead name, or business name. Record type refines your search.'}
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={`${record.recordType}-${record._id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-950">
                      {record.recordId || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${record.recordType === 'Lead' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                        {record.recordType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-medium">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span>{record.name || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {record.mobileNumber || '-'}
                        </div>
                        {record.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="h-4 w-4 text-gray-400" />
                            {record.email}
                          </div>
                        )}
                        {record.businessName && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            {record.businessName}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getGlobalStatusColor(record.status, record.recordType)}`}>
                        {getStatusLabel(record.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="space-y-1">
                        <div>{getOwnerName(record)}</div>
                        {getOwnerEmail(record) && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            {getOwnerEmail(record)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(record.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      {hasSearchTrigger && records.length > 0 && pagination.totalPages > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page || 1) - 1) * itemsPerPage + 1} to {Math.min((pagination.page || 1) * itemsPerPage, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Items per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(event) => handleItemsPerPageChange(Number(event.target.value))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => handlePageChange((pagination.page || 1) - 1)}
                disabled={loading || (pagination.page || 1) <= 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>

              <div className="flex items-center space-x-1">
                {(() => {
                  const totalPages = pagination.totalPages || 1;
                  const currentPage = pagination.page || 1;
                  const pages = [];

                  if (totalPages <= 10) {
                    for (let i = 1; i <= totalPages; i += 1) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);

                    if (currentPage <= 4) {
                      for (let i = 2; i <= 5; i += 1) {
                        pages.push(i);
                      }
                      pages.push('ellipsis');
                      pages.push(totalPages);
                    } else if (currentPage >= totalPages - 3) {
                      pages.push('ellipsis');
                      for (let i = totalPages - 4; i <= totalPages; i += 1) {
                        pages.push(i);
                      }
                    } else {
                      pages.push('ellipsis');
                      for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
                        pages.push(i);
                      }
                      pages.push('ellipsis');
                      pages.push(totalPages);
                    }
                  }

                  return pages.map((page, index) => {
                    if (page === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                          ? 'bg-agrosaf-green text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
              </div>

              <button
                type="button"
                onClick={() => handlePageChange((pagination.page || 1) + 1)}
                disabled={loading || (pagination.page || 1) >= (pagination.totalPages || 1)}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalCRMSearch;
